// src/pages/support/SupportTicket.tsx

import React, {
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";

import "./SupportTicket.css";


// =====================================================
// CONFIGURATION
// =====================================================

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000";


// =====================================================
// TYPES
// =====================================================

interface Ticket {
  id: string;
  ticket_number: string;
  user_id: string;
  ticket_type: "contact" | "problem";
  status: string;
  priority: string;
  subject: string;
  requester_name: string | null;
  requester_email: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  closed_at: string | null;
}


interface ContactMessage {
  id: string;
  ticket_id: string;
  category: string;
  message: string;
  created_at: string;
}


interface ProblemReport {
  id: string;
  ticket_id: string;
  problem_type: string;
  game_name: string | null;
  device: string;
  description: string;
  steps_to_reproduce: string | null;
  room_id: string | null;
  match_id: string | null;
  created_at: string;
}


interface SupportReply {
  id: string;
  ticket_id: string;
  sender_user_id: string | null;
  sender_type: "user" | "admin" | "system";
  message: string;
  created_at: string;
  sender_username?: string | null;
  sender_name?: string | null;
}


interface TicketResponse {
  success: boolean;
  message?: string;
  ticket?: Ticket;
  contactMessage?: ContactMessage | null;
  problemReport?: ProblemReport | null;
  replies?: SupportReply[];
}


// =====================================================
// HELPERS
// =====================================================

const formatDate = (
  value: string | null | undefined
) => {

  if (!value) {
    return "";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return date.toLocaleString();
};


const formatLabel = (
  value: string
) => {

  return value
    .replace(/_/g, " ")
    .replace(
      /\b\w/g,
      (letter) =>
        letter.toUpperCase()
    );
};


// =====================================================
// SUPPORT TICKET
// =====================================================

const SupportTicket:
  React.FC = () => {

  const {
    ticketId,
  } = useParams<{
    ticketId: string;
  }>();

  const navigate =
    useNavigate();


  const [
    ticket,
    setTicket,
  ] =
    useState<Ticket | null>(
      null
    );

  const [
    contactMessage,
    setContactMessage,
  ] =
    useState<ContactMessage | null>(
      null
    );

  const [
    problemReport,
    setProblemReport,
  ] =
    useState<ProblemReport | null>(
      null
    );

  const [
    replies,
    setReplies,
  ] =
    useState<SupportReply[]>(
      []
    );

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    error,
    setError,
  ] =
    useState("");

  const [
    replyMessage,
    setReplyMessage,
  ] =
    useState("");

  const [
    isSubmitting,
    setIsSubmitting,
  ] =
    useState(false);

  const [
    notice,
    setNotice,
  ] =
    useState("");


  // ===================================================
  // LOAD TICKET
  // ===================================================

  const loadTicket =
    useCallback(
      async () => {

        if (!ticketId) {

          setError(
            "Invalid support ticket."
          );

          setLoading(false);

          return;
        }


        const token =
          localStorage.getItem(
            "token"
          );


        if (!token) {

          navigate(
            "/login",
            {
              state: {
                returnTo:
                  `/support/tickets/${ticketId}`,
              },
            }
          );

          return;
        }


        try {

          setLoading(true);
          setError("");


          const response =
            await fetch(
              `${API_BASE_URL}/api/support/tickets/${ticketId}`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`,
                },
              }
            );


          const data:
            TicketResponse =
              await response.json();


          if (
            !response.ok ||
            !data.success ||
            !data.ticket
          ) {

            throw new Error(
              data.message ||
              "Unable to load support ticket."
            );
          }


          setTicket(
            data.ticket
          );

          setContactMessage(
            data.contactMessage ||
            null
          );

          setProblemReport(
            data.problemReport ||
            null
          );

          setReplies(
            data.replies ||
            []
          );

        } catch (err) {

          console.error(
            "Load support ticket error:",
            err
          );

          setError(
            err instanceof Error
              ? err.message
              : "Unable to load support ticket."
          );

        } finally {

          setLoading(false);
        }

      },
      [
        ticketId,
        navigate,
      ]
    );


  useEffect(
    () => {

      loadTicket();

    },
    [
      loadTicket,
    ]
  );


  // ===================================================
  // SEND REPLY
  // ===================================================

  const handleReply =
    async (
      event:
        FormEvent<HTMLFormElement>
    ) => {

      event.preventDefault();


      if (
        !ticketId ||
        !replyMessage.trim() ||
        isSubmitting
      ) {
        return;
      }


      const token =
        localStorage.getItem(
          "token"
        );


      if (!token) {

        navigate(
          "/login",
          {
            state: {
              returnTo:
                `/support/tickets/${ticketId}`,
            },
          }
        );

        return;
      }


      try {

        setIsSubmitting(true);
        setNotice("");
        setError("");


        const response =
          await fetch(
            `${API_BASE_URL}/api/support/tickets/${ticketId}/replies`,
            {
              method:
                "POST",

              headers: {
                "Content-Type":
                  "application/json",

                Authorization:
                  `Bearer ${token}`,
              },

              body:
                JSON.stringify({
                  message:
                    replyMessage.trim(),
                }),
            }
          );


        const data =
          await response.json();


        if (
          !response.ok ||
          !data.success
        ) {

          throw new Error(
            data.message ||
            "Unable to send reply."
          );
        }


        setReplyMessage("");

        setNotice(
          "Your reply has been sent to P-ARENA Support."
        );


        await loadTicket();

      } catch (err) {

        console.error(
          "Support reply error:",
          err
        );

        setError(
          err instanceof Error
            ? err.message
            : "Unable to send reply."
        );

      } finally {

        setIsSubmitting(false);
      }
    };


  // ===================================================
  // LOADING
  // ===================================================

  if (loading) {

    return (
      <main className="support-ticket-page">

        <div className="support-ticket-state">

          <div className="support-ticket-loader" />

          <h2>
            Loading Support Ticket
          </h2>

          <p>
            Retrieving your conversation
            with P-ARENA Support.
          </p>

        </div>

      </main>
    );
  }


  // ===================================================
  // ERROR
  // ===================================================

  if (
    error &&
    !ticket
  ) {

    return (
      <main className="support-ticket-page">

        <div className="support-ticket-state">

          <div className="support-ticket-state-icon">
            !
          </div>

          <h2>
            Unable to Open Ticket
          </h2>

          <p>
            {error}
          </p>

          <Link
            to="/help"
            className="support-ticket-primary-link"
          >
            Back to Help Center
          </Link>

        </div>

      </main>
    );
  }


  if (!ticket) {
    return null;
  }


  const isClosed =
    ticket.status ===
    "closed";


  // ===================================================
  // UI
  // ===================================================

  return (
    <main className="support-ticket-page">

      {/* ============================================= */}
      {/* HERO                                          */}
      {/* ============================================= */}

      <section className="support-ticket-hero">

        <div className="support-ticket-hero-glow" />

        <div className="support-ticket-hero-content">

          <nav
            className="support-ticket-breadcrumb"
            aria-label="Breadcrumb"
          >

            <Link to="/">
              P-ARENA
            </Link>

            <span>/</span>

            <Link to="/help">
              Support
            </Link>

            <span>/</span>

            <strong>
              {ticket.ticket_number}
            </strong>

          </nav>


          <p className="support-ticket-eyebrow">
            P-ARENA SUPPORT
          </p>

          <h1>
            Support Ticket
          </h1>

          <p className="support-ticket-number">
            {ticket.ticket_number}
          </p>

        </div>

      </section>


      {/* ============================================= */}
      {/* CONTENT                                       */}
      {/* ============================================= */}

      <div className="support-ticket-container">

        {/* =========================================== */}
        {/* HEADER CARD                                 */}
        {/* =========================================== */}

        <section className="support-ticket-summary">

          <div>

            <span className="support-ticket-label">
              Subject
            </span>

            <h2>
              {formatLabel(
                ticket.subject
              )}
            </h2>

            <p>
              Submitted{" "}
              {formatDate(
                ticket.created_at
              )}
            </p>

          </div>


          <div className="support-ticket-badges">

            <span
              className={
                `support-ticket-status support-ticket-status-${ticket.status}`
              }
            >
              {formatLabel(
                ticket.status
              )}
            </span>

            <span className="support-ticket-priority">
              {formatLabel(
                ticket.priority
              )} Priority
            </span>

          </div>

        </section>


        {/* =========================================== */}
        {/* ORIGINAL CONTACT MESSAGE                    */}
        {/* =========================================== */}

        {contactMessage && (

          <section className="support-ticket-original">

            <div className="support-ticket-section-heading">

              <span>
                Original Message
              </span>

              <small>
                {formatDate(
                  contactMessage.created_at
                )}
              </small>

            </div>

            <div className="support-ticket-message-card user">

              <div className="support-ticket-message-avatar">
                {(
                  ticket.requester_name ||
                  "U"
                )
                  .charAt(0)
                  .toUpperCase()}
              </div>

              <div>

                <strong>
                  {ticket.requester_name ||
                    "You"}
                </strong>

                <p>
                  {contactMessage.message}
                </p>

              </div>

            </div>

          </section>

        )}


        {/* =========================================== */}
        {/* ORIGINAL PROBLEM REPORT                     */}
        {/* =========================================== */}

        {problemReport && (

          <section className="support-ticket-original">

            <div className="support-ticket-section-heading">

              <span>
                Problem Report
              </span>

              <small>
                {formatDate(
                  problemReport.created_at
                )}
              </small>

            </div>


            <div className="support-ticket-problem-card">

              <div className="support-ticket-problem-grid">

                <div>
                  <small>
                    Problem Type
                  </small>

                  <strong>
                    {formatLabel(
                      problemReport.problem_type
                    )}
                  </strong>
                </div>


                <div>
                  <small>
                    Game
                  </small>

                  <strong>
                    {problemReport.game_name ||
                      "Not specified"}
                  </strong>
                </div>


                <div>
                  <small>
                    Device
                  </small>

                  <strong>
                    {problemReport.device}
                  </strong>
                </div>


                {problemReport.room_id && (

                  <div>
                    <small>
                      Room ID
                    </small>

                    <strong>
                      {problemReport.room_id}
                    </strong>
                  </div>

                )}


                {problemReport.match_id && (

                  <div>
                    <small>
                      Match ID
                    </small>

                    <strong>
                      {problemReport.match_id}
                    </strong>
                  </div>

                )}

              </div>


              <div className="support-ticket-problem-description">

                <small>
                  Description
                </small>

                <p>
                  {problemReport.description}
                </p>

              </div>


              {problemReport.steps_to_reproduce && (

                <div className="support-ticket-problem-description">

                  <small>
                    Steps to Reproduce
                  </small>

                  <p>
                    {problemReport.steps_to_reproduce}
                  </p>

                </div>

              )}

            </div>

          </section>

        )}


        {/* =========================================== */}
        {/* CONVERSATION                                */}
        {/* =========================================== */}

        <section className="support-ticket-conversation">

          <div className="support-ticket-section-heading">

            <span>
              Conversation
            </span>

            <small>
              {replies.length}{" "}
              {replies.length === 1
                ? "reply"
                : "replies"}
            </small>

          </div>


          {replies.length === 0 ? (

            <div className="support-ticket-no-replies">

              <strong>
                Waiting for P-ARENA Support
              </strong>

              <p>
                Your request has been received.
                A support response will appear
                here.
              </p>

            </div>

          ) : (

            <div className="support-ticket-messages">

              {replies.map(
                (reply) => {

                  const isAdmin =
                    reply.sender_type ===
                    "admin";

                  return (
                    <article
                      key={reply.id}
                      className={
                        `support-ticket-message-card ${
                          isAdmin
                            ? "admin"
                            : "user"
                        }`
                      }
                    >

                      <div className="support-ticket-message-avatar">

                        {isAdmin
                          ? "P"
                          : (
                              reply.sender_name ||
                              reply.sender_username ||
                              "U"
                            )
                              .charAt(0)
                              .toUpperCase()}

                      </div>


                      <div className="support-ticket-message-body">

                        <div className="support-ticket-message-meta">

                          <strong>
                            {isAdmin
                              ? "P-ARENA Support"
                              : "You"}
                          </strong>

                          <span>
                            {formatDate(
                              reply.created_at
                            )}
                          </span>

                        </div>

                        <p>
                          {reply.message}
                        </p>

                      </div>

                    </article>
                  );
                }
              )}

            </div>

          )}

        </section>


        {/* =========================================== */}
        {/* REPLY                                       */}
        {/* =========================================== */}

        <section className="support-ticket-reply-card">

          <div className="support-ticket-section-heading">

            <span>
              Reply to Support
            </span>

          </div>


          {isClosed ? (

            <div className="support-ticket-closed">

              <strong>
                This ticket is closed.
              </strong>

              <p>
                Closed support tickets cannot
                receive additional replies.
              </p>

            </div>

          ) : (

            <form
              onSubmit={
                handleReply
              }
            >

              <textarea
                value={replyMessage}
                onChange={(event) =>
                  setReplyMessage(
                    event.target.value
                  )
                }
                placeholder="Write your reply to P-ARENA Support..."
                rows={6}
                maxLength={5000}
                required
              />

              <div className="support-ticket-reply-footer">

                <span>
                  {replyMessage.length} / 5000
                </span>

                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !replyMessage.trim()
                  }
                >
                  {isSubmitting
                    ? "Sending..."
                    : "Send Reply"}
                </button>

              </div>

            </form>

          )}


          {notice && (

            <div className="support-ticket-success">
              {notice}
            </div>

          )}


          {error && ticket && (

            <div className="support-ticket-error">
              {error}
            </div>

          )}

        </section>

      </div>

    </main>
  );
};


export default SupportTicket;